import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
} from '@mui/material';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { geocodeByAddress } from 'react-places-autocomplete';

const containerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 37.9838,
  lng: 23.7275,
};

const MapModal = (props) => {
  const [selectedAddress, setSelectedAddress] = useState(props.address || '');
  const [modalOpen, setModalOpen] = useState(false);
  const [center, setCenter] = useState(defaultCenter);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAvDka_HEWufYVqW_m2KzIfSJEjd9O-gYI',
  });

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setSelectedMarker({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const address = results[0].formatted_address;
        setSelectedAddress(address);
        setCenter({ lat, lng });
      }
    });
  }, []);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === 'OK') {
        setSearchResults(results);
      }
    });
  };

  const handleSelect = async (result) => {
    try {
      const results = await geocodeByAddress(result.formatted_address);
      const selected = results[0];
      setSelectedAddress(selected.formatted_address);
      const location = selected.geometry.location.toJSON();
      setCenter(location);
      setSearchResults([]);
      setSelectedMarker(location);
    } catch (error) {
      console.error(error);
    }
  };

  const centerOnAddress = (address) => {
    if (!address) return;
    geocodeByAddress(address)
      .then((res) => {
        const location = res[0].geometry.location.toJSON();
        setCenter(location);
        setSearchResults([]);
        setSelectedMarker(location);
      })
      .catch((err) => console.error(err));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (loadError) {
    return <Button variant="outlined" disabled>Map Error</Button>;
  }

  return (
    <div>
      <Button variant="outlined" onClick={toggleModal}>
        Map
      </Button>

      <Dialog open={modalOpen} onClose={toggleModal} maxWidth="md" fullWidth>
        <DialogTitle>Select a location on the map</DialogTitle>
        <DialogContent>
          <Box sx={{ height: '400px', width: '100%', position: 'relative', mb: 2 }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={13}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onClick={handleMapClick}
              >
                {selectedMarker && (
                  <Marker position={selectedMarker} />
                )}
              </GoogleMap>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography>Loading map...</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Search for an address"
            />
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Box>

          {searchResults.length > 0 && (
            <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
              {searchResults.map((result) => (
                <ListItem
                  key={result.place_id}
                  button
                  onClick={() => handleSelect(result)}
                  sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <ListItemText primary={result.formatted_address} />
                </ListItem>
              ))}
            </List>
          )}

          {selectedAddress && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Selected: {selectedAddress}
            </Typography>
          )}

          {props.address && (
            <Button size="small" variant="text" onClick={() => centerOnAddress(props.address)}>
              Center on current address
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              props.onAddressSelect(selectedAddress);
              toggleModal();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

MapModal.propTypes = {
  onAddressSelect: PropTypes.func.isRequired,
  address: PropTypes.any,
};

export default MapModal;
